import { cn } from "cn";
import type { FC } from "react";
import React from "react";

import { hasValue } from "@core/foundation/types";

import { MediaThumb } from "@react/domains/media";
import { Badge } from "@react/ui-primitives/badge";

import type { Generation } from "@apis/generation/models";
import type { MediaAssetPreview } from "@apis/media/api";

interface GenerationStatusBadgeProps {
  readonly status: Generation["status"];
}

const GenerationStatusBadge: FC<GenerationStatusBadgeProps> = ({ status }) => {
  switch (status) {
    case "completed":
      return <Badge variant="secondary">Completed</Badge>;
    case "failed":
      return <Badge variant="destructive">Failed</Badge>;
    case "pending":
      return <Badge variant="outline">Pending</Badge>;
    case "processing":
      return <Badge variant="outline">Processing</Badge>;
  }
};

interface GenerationCardProps {
  readonly className?: string;
  readonly generation: Generation;
  readonly images: readonly MediaAssetPreview[];
}

export const GenerationCard: FC<GenerationCardProps> = ({
  className,
  generation,
  images,
}) => (
  <div className={cn("flex flex-col gap-2 rounded-md border p-3", className)}>
    <div className="flex items-center justify-between gap-2">
      <span className="text-sm font-medium">{generation.request.model}</span>
      <GenerationStatusBadge status={generation.status} />
    </div>
    <p className="text-sm whitespace-pre-wrap">{generation.description}</p>
    <p className="text-sm whitespace-pre-wrap text-muted-foreground">
      {generation.request.prompt}
    </p>
    {hasValue(generation.error) && (
      <p className="text-sm text-destructive">{generation.error}</p>
    )}
    {images.length > 0 && (
      <div
        className={`
          grid grid-cols-2 gap-3
          sm:grid-cols-3
          lg:grid-cols-4
        `}
      >
        {images.map(({ mediaAsset, previewUrl }) => (
          <MediaThumb
            asset={mediaAsset}
            key={mediaAsset.id}
            previewUrl={previewUrl}
          />
        ))}
      </div>
    )}
  </div>
);
